/**
 * Admin → one site (container): its servers (live: every link change, health
 * report, role change, code issued, used or cancelled refetches them — the
 * change channel's site and node kinds, D35), the codes still waiting for a
 * server, "Add server" with the generated `docker run` line, make the backup
 * main, replace a server (confirmed by typing its label; the replacement's
 * install line follows), remove one for good (typed too; a refusal because it
 * runs productions offers "Remove anyway"), forget a revoked one's record,
 * cancel a pending code, and the site's emergency account (the password shown
 * once).
 */
import { ApiError } from '@acp/api-client/client.js';
import type { SitePairNodeWire } from '@acp/api-contract/pairStatus.js';
import type { EnrolmentCodeWire, PendingEnrolmentWire, SiteWire, SiteNodeWire } from '@acp/api-contract/sites.js';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import {
  useAdminSites,
  useCancelEnrolment,
  useConnectivity,
  useDeleteNode,
  useEmergencyAccount,
  useEmergencyAccountWrite,
  useEnrolmentCode,
  useNow,
  usePendingEnrolments,
  useRedundancyOverview,
  useRemoveNode,
  useReplaceNode,
  useSiteNodes,
  useSwapRoles,
} from '../../app/hooks.js';
import { AddServerDialog } from '../../components/admin/AddServerDialog.js';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog.js';
import { EmergencyAccountCard } from '../../components/admin/EmergencyAccountCard.js';
import { NodesTable } from '../../components/admin/NodesTable.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card.js';
import { reachHintsByNode } from '../../connectivity/reachHint.js';
import { RedundancyPanel } from '../RedundancyView.js';
import { copyText, errorText, messageOf } from './common.js';

/** A replacement's code lives the longest D21 allows: the new host may take a while to set up. */
const REPLACEMENT_CODE_MINUTES = 24 * 60;

export function SiteAdminView({ siteId }: { readonly siteId: string }) {
  const sites = useAdminSites();
  const nodes = useSiteNodes(siteId);
  const emergency = useEmergencyAccount(siteId);
  const writeEmergency = useEmergencyAccountWrite();
  const code = useEnrolmentCode();
  const swap = useSwapRoles();
  const replace = useReplaceNode();
  const nowMs = useNow(30_000);
  // Why this browser cannot reach a connected server by its name (D16): the connectivity manager probes every node.
  const reachHints = reachHintsByNode(useConnectivity());
  const [adding, setAdding] = useState(false);
  const [enrolment, setEnrolment] = useState<EnrolmentCodeWire | null>(null);
  const [replacing, setReplacing] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const removal = useServerRemoval(siteId, nodes.data ?? []);

  const site = sites.data?.find((s) => s.siteId === siteId);
  const onprem = site?.kind !== 'cloud';
  const { pendingEnrolments, pairNodes } = useServerListExtras(siteId, site);
  const replacingNode = nodes.data?.find((n) => n.nodeId === replacing);

  const writePassword = (rotate: boolean) =>
    writeEmergency.mutate(
      { siteId, rotate },
      {
        onSuccess: (result) => {
          setRotating(false);
          setRevealed(result.password);
        },
      },
    );

  return (
    <div className="grid gap-4" data-testid="site-page">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="grid gap-1.5">
            <CardTitle>{site?.name ?? siteId}</CardTitle>
            <CardDescription>{siteDescription(site)}</CardDescription>
          </div>
          {onprem ? (
            <Button
              onClick={() => {
                code.reset();
                setEnrolment(null);
                setAdding(true);
              }}
              data-testid="add-server"
            >
              Add server
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <NodesTable
            nodes={nodes.data ?? []}
            pendingEnrolments={pendingEnrolments}
            pairNodes={pairNodes}
            nowMs={nowMs}
            loading={nodes.isFetching}
            error={errorText(nodes.error)}
            pending={swap.isPending || replace.isPending || removal.busy}
            onRetry={() => {
              void nodes.refetch();
            }}
            onMakeMain={(nodeId) =>
              swap.mutate({ siteId, mainNodeId: nodeId }, { onSuccess: () => toast.success('Roles swapped: the backup is main now'), onError: (err) => toast.error(messageOf(err)) })
            }
            onReplace={(nodeId) => {
              replace.reset();
              setReplacing(nodeId);
            }}
            onRemove={removal.onRemove}
            onDelete={removal.onDelete}
            onCancelEnrolment={removal.onCancelEnrolment}
            reachHints={reachHints}
          />
        </CardContent>
      </Card>
      {/* The site's pairs (wave 7b-tail C): every production with its active and standby node, graded. */}
      <RedundancyPanel siteId={siteId} />
      {onprem ? (
        <EmergencyAccountCard
          status={emergency.data ?? null}
          revealed={revealed}
          pending={writeEmergency.isPending}
          error={rotating ? null : errorText(writeEmergency.error)}
          onCreate={() => writePassword(false)}
          onRotate={() => {
            writeEmergency.reset();
            setRotating(true);
          }}
          onCopy={(text) => copyText(text, 'Password')}
          onDismiss={() => setRevealed(null)}
        />
      ) : null}
      <AddServerDialog
        open={adding}
        siteName={site?.name ?? siteId}
        enrolment={enrolment}
        pending={code.isPending}
        error={errorText(code.error)}
        onGenerate={(request) => code.mutate({ siteId, ...request }, { onSuccess: setEnrolment })}
        onCopy={(text) => copyText(text, 'Install command')}
        onOpenChange={(open) => {
          setAdding(open);
          if (!open) setEnrolment(null);
        }}
      />
      <ConfirmDialog
        open={replacing !== null}
        title={`Replace ${replacingNode?.label ?? 'node'}?`}
        description="Its key is revoked for good — it is refused from now on, its link is closed and its DNS name withdrawn — and you get the install command for its successor, which takes over its role."
        confirmLabel="Revoke and replace"
        typeToConfirm={replacingNode?.label ?? null}
        pending={replace.isPending}
        error={errorText(replace.error)}
        onConfirm={() => {
          if (replacing === null) return;
          replace.mutate(
            { siteId, nodeId: replacing, minutes: REPLACEMENT_CODE_MINUTES },
            {
              onSuccess: (successor) => {
                setReplacing(null);
                setEnrolment(successor);
                setAdding(true);
              },
            },
          );
        }}
        onOpenChange={(open) => (open ? undefined : setReplacing(null))}
      />
      {removal.dialogs}
      <ConfirmDialog
        open={rotating}
        title="Rotate the emergency password?"
        description="The old password stops working, and sessions opened with it end at the servers’ next sync. The new one is shown once."
        confirmLabel="Rotate password"
        typeToConfirm={null}
        pending={writeEmergency.isPending}
        error={errorText(writeEmergency.error)}
        onConfirm={() => writePassword(true)}
        onOpenChange={setRotating}
      />
    </div>
  );
}

function siteDescription(site: SiteWire | undefined): string {
  if (site?.kind === 'cloud') return 'The cloud site: its nodes are your cloud productions’ tasks, started and stopped with them.';
  const studios = site === undefined || site.studios.length === 0 ? 'none yet' : site.studios.join(', ');
  return `Servers of this site. Studios here: ${studios}.`;
}

/**
 * The server list's other two sources: the codes still waiting for a server (an on-prem site's only — the cloud site
 * takes no codes), and the pair reports (the same query the redundancy panel reads) for the peer-link and witness
 * columns.
 */
function useServerListExtras(siteId: string, site: SiteWire | undefined): { pendingEnrolments: readonly PendingEnrolmentWire[]; pairNodes: readonly SitePairNodeWire[] } {
  const onprem = site !== undefined && site.kind !== 'cloud';
  const pendingCodes = usePendingEnrolments(siteId, onprem);
  const overview = useRedundancyOverview(siteId);
  return {
    pendingEnrolments: onprem ? (pendingCodes.data ?? []) : [],
    pairNodes: overview.data?.sites.find((s) => s.siteId === siteId)?.nodes ?? [],
  };
}

/**
 * "Remove", "Delete record" and "Cancel code": their mutations, which one is being confirmed, and the three
 * confirmations. A removal the cloud refuses (`conflict`: the server runs productions, or is the last one) turns the
 * dialog into "Remove anyway", which sends `force`.
 */
function useServerRemoval(siteId: string, nodes: readonly SiteNodeWire[]): { busy: boolean; onRemove: (nodeId: string) => void; onDelete: (nodeId: string) => void; onCancelEnrolment: (codeId: string) => void; dialogs: ReactNode } {
  const remove = useRemoveNode();
  const forget = useDeleteNode();
  const cancelCode = useCancelEnrolment();
  const [removing, setRemoving] = useState<string | null>(null);
  const [forgetting, setForgetting] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const removingNode = nodes.find((n) => n.nodeId === removing);
  const forgettingNode = nodes.find((n) => n.nodeId === forgetting);
  const removalRefused = remove.error instanceof ApiError && remove.error.code === 'conflict';
  const dialogs = (
    <>
      <ConfirmDialog
        open={removing !== null}
        title={`Remove ${removingNode?.label ?? 'server'}?`}
        description={
          removalRefused
            ? `${errorText(remove.error) ?? ''} Removing it anyway takes it off air at once.`
            : `It is revoked for good — refused from now on, its link closed, its DNS name withdrawn — and no replacement code is issued.${removingNode?.role === 'main' ? ' The backup, if the site has one, becomes main.' : ''} To swap the hardware instead, use Replace.`
        }
        confirmLabel={removalRefused ? 'Remove anyway' : 'Remove server'}
        typeToConfirm={removingNode?.label ?? null}
        pending={remove.isPending}
        error={removalRefused ? null : errorText(remove.error)}
        onConfirm={() => {
          if (removing === null) return;
          remove.mutate(
            { siteId, nodeId: removing, force: removalRefused },
            {
              onSuccess: (result) => {
                setRemoving(null);
                toast.success(result.promotedNodeId === null ? 'Server removed' : 'Server removed; the backup is main now');
              },
            },
          );
        }}
        onOpenChange={(open) => (open ? undefined : setRemoving(null))}
      />
      <ConfirmDialog
        open={forgetting !== null}
        title={`Delete the record of ${forgettingNode?.label ?? 'this server'}?`}
        description="The replaced server leaves this list. Its key stays refused; nothing else changes."
        confirmLabel="Delete record"
        typeToConfirm={null}
        pending={forget.isPending}
        error={errorText(forget.error)}
        onConfirm={() => {
          if (forgetting === null) return;
          forget.mutate({ siteId, nodeId: forgetting }, { onSuccess: () => setForgetting(null) });
        }}
        onOpenChange={(open) => (open ? undefined : setForgetting(null))}
      />
      <ConfirmDialog
        open={cancelling !== null}
        title="Cancel this enrolment code?"
        description="A server started with it is refused from now on. Its row goes; issue a new code with “Add server” if you still need one."
        confirmLabel="Cancel code"
        typeToConfirm={null}
        pending={cancelCode.isPending}
        error={errorText(cancelCode.error)}
        onConfirm={() => {
          if (cancelling === null) return;
          cancelCode.mutate({ siteId, codeId: cancelling }, { onSuccess: () => setCancelling(null) });
        }}
        onOpenChange={(open) => (open ? undefined : setCancelling(null))}
      />
    </>
  );
  return {
    busy: remove.isPending || forget.isPending || cancelCode.isPending,
    onRemove: (nodeId) => {
      remove.reset();
      setRemoving(nodeId);
    },
    onDelete: (nodeId) => {
      forget.reset();
      setForgetting(nodeId);
    },
    onCancelEnrolment: (codeId) => {
      cancelCode.reset();
      setCancelling(codeId);
    },
    dialogs,
  };
}
